import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Phone, PhoneOff, PhoneCall, Timer, MessageSquareText, Trophy,
  ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────
interface WeekWin { weekStart: string; weekEnd: string; weekLabel: string }
type Outcome = "answered" | "voicemail" | "abandoned" | "missed";
interface WeekAgg {
  win: WeekWin;
  totalInbound: number;
  outcomes: Record<Outcome, number>;
  unansweredPct: number | null;
  medianPickupSec: number;
  avgTalkSec: number;
  handupBuckets: { lt10: number; s10_30: number; s30_60: number; gte60: number; total: number };
  bySegment: {
    existing:     { total: number; answered: number; voicemail: number; abandoned: number; missed: number; medianPickup: number };
    new_prospect: { total: number; answered: number; voicemail: number; abandoned: number; missed: number; medianPickup: number };
  };
  ringDistribution: {
    existing:     { lt10: number; s10_30: number; s30_60: number; gte60: number; total: number };
    new_prospect: { lt10: number; s10_30: number; s30_60: number; gte60: number; total: number };
  };
  agentActivity: Array<{ agent: string; outbound: number; daysActive: number; perWeekday: number }>;
}
interface Response {
  thisWeek: WeekAgg;
  prevWeek: WeekAgg;
  trend: Array<{ weekStart: string; totalInbound: number; unansweredPct: number | null }>;
  generatedAt: string;
}

// ── Formatters ─────────────────────────────────────────────────────────
const dash = "–";
function pct(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return `${(n * 100).toFixed(digits)}%`;
}
function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return n.toLocaleString("en-GB");
}
function fmtSec(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n <= 0) return dash;
  return `${Math.round(n)}s`;
}
function fmtMinSec(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n <= 0) return dash;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}
function fmtDate(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" });
}

// Simple delta calculator: returns { arrow, cls, text } for showing WoW change
function delta(now: number | null, prev: number | null, opts: { lowerIsBetter?: boolean; asPct?: boolean } = {}) {
  if (now === null || prev === null || !Number.isFinite(now) || !Number.isFinite(prev)) {
    return { arrow: <Minus className="h-3 w-3" />, cls: "text-muted-foreground", text: "" };
  }
  const diff = now - prev;
  const isBad = opts.lowerIsBetter ? diff > 0 : diff < 0;
  const isGood = opts.lowerIsBetter ? diff < 0 : diff > 0;
  const cls = isBad ? "text-red-600" : isGood ? "text-emerald-600" : "text-muted-foreground";
  const arrow = isGood ? <TrendingUp className="h-3 w-3" /> : isBad ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />;
  const text = opts.asPct ? `${diff >= 0 ? "+" : ""}${(diff * 100).toFixed(1)}pp` : `${diff >= 0 ? "+" : ""}${Math.round(diff)}`;
  return { arrow, cls, text };
}

// ── Page ───────────────────────────────────────────────────────────────
export default function WeeklyPhoneReport() {
  const [weekAnchor, setWeekAnchor] = useState<string | null>(null);
  const qs = weekAnchor ? `?week=${weekAnchor}` : "";
  const { data, isLoading, error } = useQuery<Response>({
    queryKey: ["weekly-phone-report", weekAnchor],
    queryFn: async () => {
      const res = await fetch(`/api/reports/weekly-phone${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading weekly phone report…</div>;
  if (error || !data) return <div className="p-6 text-red-600">Failed: {error instanceof Error ? error.message : "unknown"}</div>;

  const t = data.thisWeek;
  const p = data.prevWeek;

  const shiftWeek = (n: number) => {
    const cur = new Date(t.win.weekStart + "T12:00:00Z");
    const shifted = new Date(cur.getTime() + n * 7 * 86400_000);
    setWeekAnchor(shifted.toISOString().slice(0, 10));
  };

  // Story generator
  const story = generateStory(t, p);

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: "#0b1c3a" }}>
            <Phone className="h-6 w-6" /> Weekly Phone Performance Report
          </h1>
          <p className="text-sm text-muted-foreground">
            {t.win.weekLabel} — vs previous week ({p.win.weekLabel})
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)}>
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekAnchor(null)}>Latest</Button>
          <Button variant="outline" size="sm" onClick={() => shiftWeek(1)}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* 1. Hero KPI band — pure call statistics only */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HeroCard icon={<Phone className="h-3.5 w-3.5" />} label="Total inbound"
          value={fmtInt(t.totalInbound)} d={delta(t.totalInbound, p.totalInbound)} />
        <HeroCard icon={<PhoneOff className="h-3.5 w-3.5" />} label="Unanswered %"
          value={pct(t.unansweredPct)} d={delta(t.unansweredPct, p.unansweredPct, { lowerIsBetter: true, asPct: true })}
          tone={(t.unansweredPct ?? 0) >= 0.35 ? "bad" : (t.unansweredPct ?? 0) >= 0.20 ? "warn" : "good"} />
        <HeroCard icon={<Timer className="h-3.5 w-3.5" />} label="Median pickup"
          value={fmtSec(t.medianPickupSec)} d={delta(t.medianPickupSec, p.medianPickupSec, { lowerIsBetter: true })}
          tone={t.medianPickupSec <= 15 ? "good" : "warn"} />
        <HeroCard icon={<MessageSquareText className="h-3.5 w-3.5" />} label="Avg talk time"
          value={fmtMinSec(t.avgTalkSec)} d={delta(t.avgTalkSec, p.avgTalkSec)} />
      </div>

      {/* 2. Call outcome mix — donuts side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <OutcomeDonut title={`This week — ${t.win.weekLabel}`} outcomes={t.outcomes} total={t.totalInbound} />
        <OutcomeDonut title={`Previous — ${p.win.weekLabel}`} outcomes={p.outcomes} total={p.totalInbound} muted />
      </div>

      {/* 3. Hang-up patience */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Hang-ups before voicemail — patience profile</CardTitle>
          <p className="text-xs text-muted-foreground">
            {t.handupBuckets.total} people hung up before reaching voicemail this week.
          </p>
        </CardHeader>
        <CardContent>
          <PatienceBars b={t.handupBuckets} />
        </CardContent>
      </Card>

      {/* 4. Existing vs New */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SegmentCard title="Existing customers" seg={t.bySegment.existing} accent="emerald" />
        <SegmentCard title="New prospects" seg={t.bySegment.new_prospect} accent="blue" />
      </div>

      {/* 5. Ring / pickup speed distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pickup speed distribution — answered calls</CardTitle>
          <p className="text-xs text-muted-foreground">
            How long callers waited before someone picked up. Existing median <b>{t.bySegment.existing.medianPickup}s</b> · New median <b>{t.bySegment.new_prospect.medianPickup}s</b>.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RingDist title="Existing" dist={t.ringDistribution.existing} accent="emerald" />
            <RingDist title="New prospect" dist={t.ringDistribution.new_prospect} accent="blue" />
          </div>
        </CardContent>
      </Card>

      {/* 6. Agent activity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4" /> Who's on the phones — outbound activity</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="py-1">Agent</th>
                <th className="text-right">Outbound calls</th>
                <th className="text-right">Days active</th>
                <th className="text-right">Calls per weekday</th>
              </tr>
            </thead>
            <tbody>
              {t.agentActivity.map((a, i) => (
                <tr key={a.agent} className={`border-t ${i === 0 ? "bg-emerald-50/40 dark:bg-emerald-950/20 font-semibold" : ""}`}>
                  <td className="py-1.5 pr-2">{i === 0 && "🥇 "}{a.agent}</td>
                  <td className="text-right font-mono">{fmtInt(a.outbound)}</td>
                  <td className="text-right font-mono">{fmtInt(a.daysActive)}</td>
                  <td className="text-right font-mono">{a.perWeekday.toFixed(1)}</td>
                </tr>
              ))}
              {t.agentActivity.length === 0 && (
                <tr><td colSpan={4} className="py-3 text-muted-foreground">No outbound activity this week.</td></tr>
              )}
            </tbody>
          </table>
          <div className="text-[11px] text-muted-foreground mt-3">
            Inbound answered calls all show "Main AA" as callee in the CDR — 8x8 doesn't attribute per-agent at this level. Outbound is the best proxy for who's on the phones.
          </div>
        </CardContent>
      </Card>

      {/* 8. Trend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Last 4 weeks — trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TrendChart title="Inbound calls" data={data.trend.map((w) => ({ label: fmtDate(w.weekStart), value: w.totalInbound }))} />
            <TrendChart title="Unanswered %" data={data.trend.map((w) => ({ label: fmtDate(w.weekStart), value: (w.unansweredPct ?? 0) * 100 }))} suffix="%" />
          </div>
        </CardContent>
      </Card>

      {/* 9. Story */}
      <Card className="border-2" style={{ borderColor: "#45ceff" }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">This week's story</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {story.map((line, i) => <p key={i}>{line}</p>)}
        </CardContent>
      </Card>

      {/* 10. Footer CTA */}
      <Card className="border-2" style={{ borderColor: "#45ceff", background: "linear-gradient(to right, #f0f9ff, #ecfdf5)" }}>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-center">
            <div className="flex items-center justify-center gap-2 text-base font-semibold" style={{ color: "#0b1c3a" }}>
              <PhoneCall className="h-5 w-5" /> Answer in 3 rings
            </div>
            <div className="flex items-center justify-center gap-2 text-base font-semibold" style={{ color: "#0b1c3a" }}>
              <Timer className="h-5 w-5" /> Call leads in 3 minutes
            </div>
            <div className="flex items-center justify-center gap-2 text-base font-semibold" style={{ color: "#0b1c3a" }}>
              <Trophy className="h-5 w-5" /> Book the appointment
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-[11px] text-muted-foreground text-right">
        Generated {new Date(data.generatedAt).toLocaleString("en-GB")} · Live from BigQuery — no manual data input
      </div>
    </div>
  );
}

// ── Story generator (call statistics only) ────────────────────────────
function generateStory(t: WeekAgg, p: WeekAgg): string[] {
  const lines: string[] = [];
  const inboundDelta = t.totalInbound - p.totalInbound;
  const unDelta = (t.unansweredPct ?? 0) - (p.unansweredPct ?? 0);
  const newAbandonedPct = t.bySegment.new_prospect.total > 0
    ? t.bySegment.new_prospect.abandoned / t.bySegment.new_prospect.total : 0;

  // Big picture line
  if (Math.abs(inboundDelta) > 10 || Math.abs(unDelta) > 0.05) {
    const dir = inboundDelta >= 0 ? "up" : "down";
    lines.push(`Total inbound ${dir} ${Math.abs(inboundDelta)} vs last week (${t.totalInbound} vs ${p.totalInbound}). Unanswered rate ${unDelta >= 0 ? "up" : "down"} ${Math.abs(unDelta * 100).toFixed(1)}pp to ${((t.unansweredPct ?? 0) * 100).toFixed(1)}%.`);
  } else {
    lines.push(`Call volume and answering broadly steady vs last week.`);
  }

  // Positive
  if (unDelta < -0.03) lines.push(`✓ Positive: Unanswered rate improved by ${Math.abs(unDelta * 100).toFixed(1)}pp.`);
  else if (t.medianPickupSec < p.medianPickupSec) lines.push(`✓ Positive: Median pickup faster (${t.medianPickupSec}s vs ${p.medianPickupSec}s).`);
  else if (t.avgTalkSec > p.avgTalkSec + 15) lines.push(`✓ Positive: Talk time up ${t.avgTalkSec - p.avgTalkSec}s — richer conversations.`);
  else lines.push(`Watch: nothing markedly improved vs last week.`);

  // Fix next week
  if (newAbandonedPct > 0.30) {
    lines.push(`⚠ Fix next week: ${(newAbandonedPct * 100).toFixed(0)}% of new prospects hung up before reaching a person. First-time callers won't leave voicemail — capacity, not skill.`);
  } else if (t.handupBuckets.s30_60 > 20) {
    lines.push(`⚠ Fix next week: ${t.handupBuckets.s30_60} callers hung up between 30 and 60 seconds — the "almost got them" zone. These are recoverable with faster answering.`);
  } else if (t.handupBuckets.gte60 > 10) {
    lines.push(`⚠ Fix next week: ${t.handupBuckets.gte60} callers waited over a minute and gave up. Highest-cost lost lead — they wanted to speak to us.`);
  } else {
    lines.push(`✓ No urgent fires — keep the process going.`);
  }
  return lines;
}

// ── Sub-components ─────────────────────────────────────────────────────
function HeroCard({ icon, label, value, d, tone }: {
  icon: React.ReactNode; label: string; value: string;
  d: { arrow: React.ReactNode; cls: string; text: string };
  tone?: "good" | "warn" | "bad";
}) {
  const ring = tone === "bad" ? "border-red-500/40" : tone === "warn" ? "border-amber-500/40" : tone === "good" ? "border-emerald-500/40" : "";
  const valueCls = tone === "bad" ? "text-red-600" : tone === "warn" ? "text-amber-600" : tone === "good" ? "text-emerald-600" : "";
  return (
    <Card className={ring}>
      <CardContent className="p-3">
        <div className="text-muted-foreground text-xs flex items-center justify-between">
          <span className="flex items-center gap-1.5">{icon} {label}</span>
          <span className={`text-[10px] flex items-center gap-0.5 ${d.cls}`}>{d.arrow} {d.text}</span>
        </div>
        <div className={`text-2xl font-bold mt-1 ${valueCls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

const OUTCOME_COLORS: Record<Outcome, string> = {
  answered: "#059669",
  voicemail: "#d97706",
  abandoned: "#dc2626",
  missed: "#6b7280",
};
function OutcomeDonut({ title, outcomes, total, muted }: {
  title: string; outcomes: Record<Outcome, number>; total: number; muted?: boolean;
}) {
  const order: Outcome[] = ["answered", "voicemail", "abandoned", "missed"];
  let cum = 0;
  const size = 140, R = 60, C = 2 * Math.PI * R;
  return (
    <Card className={muted ? "opacity-80" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size/2} cy={size/2} r={R} fill="none" stroke="#e5e7eb" strokeWidth="18" />
            {order.map((k) => {
              const v = outcomes[k] || 0;
              if (v === 0 || total === 0) return null;
              const frac = v / total;
              const dash = frac * C;
              const el = (
                <circle key={k} cx={size/2} cy={size/2} r={R} fill="none"
                  stroke={OUTCOME_COLORS[k]} strokeWidth="18"
                  strokeDasharray={`${dash} ${C - dash}`}
                  strokeDashoffset={-cum * C}
                  transform={`rotate(-90 ${size/2} ${size/2})`}
                />
              );
              cum += frac;
              return el;
            })}
            <text x={size/2} y={size/2 - 4} textAnchor="middle" fontSize="20" fontWeight="700" fill="#0b1c3a">{total}</text>
            <text x={size/2} y={size/2 + 14} textAnchor="middle" fontSize="10" fill="#6b7280">calls</text>
          </svg>
          <div className="flex-1 space-y-1 text-xs">
            {order.map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: OUTCOME_COLORS[k] }} />
                <span className="capitalize flex-1">{k}</span>
                <span className="font-mono">{outcomes[k] || 0}</span>
                <span className="text-muted-foreground w-10 text-right">{total > 0 ? `${((outcomes[k] || 0) / total * 100).toFixed(0)}%` : "-"}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PatienceBars({ b }: { b: { lt10: number; s10_30: number; s30_60: number; gte60: number; total: number } }) {
  const rows = [
    { label: "Under 10s",  value: b.lt10,   ann: "misdials / instant regret",     color: "#6b7280" },
    { label: "10–30s",     value: b.s10_30, ann: "lost prospect — low patience",  color: "#d97706" },
    { label: "30–60s",     value: b.s30_60, ann: "the leak zone — most recoverable", color: "#dc2626" },
    { label: "60s or more",value: b.gte60,  ann: "waited over a minute — highest cost lost lead", color: "#7c2d12" },
  ];
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex justify-between text-xs mb-1">
            <span><b>{r.label}</b> · <span className="text-muted-foreground italic">{r.ann}</span></span>
            <span className="font-mono">{r.value} ({b.total > 0 ? `${(r.value / b.total * 100).toFixed(0)}%` : "0%"})</span>
          </div>
          <div className="h-3 w-full rounded-full bg-muted/50 overflow-hidden">
            <div className="h-full" style={{ width: `${Math.max(2, (r.value / max) * 100)}%`, background: r.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SegmentCard({ title, seg, accent }: {
  title: string;
  seg: { total: number; answered: number; voicemail: number; abandoned: number; missed: number; medianPickup: number };
  accent: "emerald" | "blue";
}) {
  const border = accent === "emerald" ? "border-emerald-500/30" : "border-blue-500/30";
  const un = seg.abandoned + seg.voicemail + seg.missed;
  const unPct = seg.total > 0 ? un / seg.total : 0;
  return (
    <Card className={border}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <p className="text-xs text-muted-foreground">{seg.total} calls this week</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-2xl font-bold text-emerald-600">{pct(seg.total > 0 ? seg.answered / seg.total : 0)}</div>
            <div className="text-xs text-muted-foreground">answered · {seg.answered} calls</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">{pct(unPct)}</div>
            <div className="text-xs text-muted-foreground">unanswered · {un} calls</div>
          </div>
          <div>
            <div className="text-lg font-bold text-amber-600">{pct(seg.total > 0 ? seg.voicemail / seg.total : 0)}</div>
            <div className="text-[11px] text-muted-foreground">left voicemail · {seg.voicemail}</div>
          </div>
          <div>
            <div className="text-lg font-bold text-red-600">{pct(seg.total > 0 ? seg.abandoned / seg.total : 0)}</div>
            <div className="text-[11px] text-muted-foreground">hung up · {seg.abandoned}</div>
          </div>
        </div>
        <div className="text-xs text-muted-foreground border-t pt-2">
          Median pickup when answered: <span className="font-mono">{seg.medianPickup}s</span>
        </div>
      </CardContent>
    </Card>
  );
}

function RingDist({ title, dist, accent }: {
  title: string;
  dist: { lt10: number; s10_30: number; s30_60: number; gte60: number; total: number };
  accent: "emerald" | "blue";
}) {
  const c = accent === "emerald" ? "#059669" : "#2563eb";
  const rows = [
    { label: "Under 10s",   value: dist.lt10 },
    { label: "10–30s",       value: dist.s10_30 },
    { label: "30–60s",       value: dist.s30_60 },
    { label: "60s or more",  value: dist.gte60 },
  ];
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div>
      <div className="text-sm font-medium mb-2">{title} <span className="text-muted-foreground text-xs">({dist.total})</span></div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex justify-between text-xs">
              <span>{r.label}</span>
              <span className="font-mono">{r.value} ({dist.total > 0 ? `${(r.value / dist.total * 100).toFixed(0)}%` : "-"})</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted/50 overflow-hidden">
              <div className="h-full" style={{ width: `${Math.max(2, (r.value / max) * 100)}%`, background: c }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendChart({ title, data, suffix = "" }: { title: string; data: Array<{ label: string; value: number }>; suffix?: string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div>
      <div className="text-xs font-medium mb-2">{title}</div>
      <div className="space-y-1.5">
        {data.map((d, i) => (
          <div key={i}>
            <div className="flex justify-between text-[11px]">
              <span className="text-muted-foreground">{d.label}</span>
              <span className="font-mono">{Math.round(d.value)}{suffix}</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted/50 overflow-hidden">
              <div className="h-full bg-sky-500" style={{ width: `${Math.max(2, (d.value / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
